import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Device } from '@capacitor/device';
import { BehaviorSubject, from, of, switchMap, take, tap } from 'rxjs';
import { StorageService } from './storage.service';

const CURRRENT_ADDRESS_KEY = 'CURRENT_ADDRESS';

export interface City {
  value: string;
  details: string;
  id: number;
}

export interface Street {
  value: string;
  id: number;
}

export interface HouseNumber {
  value: string;
  id: number;
}


export interface Address {
  id?: string,
  label?: string,
  street?: string,
  street_id?: number,
  city?: string,
  city_id?: number,
  number?: string,
  commission_point_id?: string,
  main?: boolean,
  device_id?: string,
  notification_interval?: number,
  notification_hour?: number,
}

@Injectable({
  providedIn: 'root'
})
export class AddressService {
  private _http = inject(HttpClient);
  private _storage = inject(StorageService);

  private _cities$ = new BehaviorSubject<Array<City>>([]);
  public cities$ = this._cities$.asObservable();

  private _streets$ = new BehaviorSubject<Array<Street>>([]);
  public streets$ = this._streets$.asObservable();

  private _numbers$ = new BehaviorSubject<Array<HouseNumber>>([]);
  public numbers$ = this._numbers$.asObservable();

  private _newAddressRequest?: Address = {};
  public set newAddressRequest(value: Address) {
    this._newAddressRequest = {
      ...this._newAddressRequest,
      ...value
    }
  }
  public get newAddressRequest() {
    return this._newAddressRequest || {};
  }

  private _currentAddress!: Address;
  private _currentAddress$ = new BehaviorSubject<Address>({});
  public currentAddress$ = this._currentAddress$.asObservable();

  private _addresses: Array<Address> = [];
  private _addresses$ = new BehaviorSubject<Array<Address>>([]);
  public addresses$ = this._addresses$.asObservable();

  public getCities() {
    return this._http
      .get<Array<City>>('/cities')
      .pipe(
        tap(response => this._cities$.next(response)),
        // tap(response => console.log('Cities ==> ', response))
      );
  }

  public getStreets(params: City) {
    const { id, value } = params;

    this.newAddressRequest = {
      city_id: id,
      city: value
    };

    return this._http
      .get<Array<Street>>(`/streets`, {
        params: { city_id: id }
      })
      .pipe(
        tap(response => this._streets$.next(response)),
        // tap(response => console.log('Streets ==> ', response))
      );
  }

  public getHouseNumbers(street: Street) {
    const { city_id } = this.newAddressRequest;
    const { id } = street;

    this.newAddressRequest = {
      street_id: street.id,
      street: street.value
    };

    if (!city_id) return of();

    return this._http
      .get<Array<HouseNumber>>(`/numbers`, {
        params: {
          city_id,
          street_id: id
        }
      })
      .pipe(
        tap(response => this._numbers$.next(response))
      );
  }

  public getAddresses() {
    return from(Device.getId())
      .pipe(
        take(1),
        switchMap((deviceId) => {
          const { identifier } = deviceId;

          return this._http
            .get<Array<Address>>('/addresses', {
              params: {
                device_id: identifier
              }
            })
        }),
        tap((addresses) => {
          // console.log('Addresses ==> ', addresses);
          if (addresses.length) {
            this._addresses = addresses;
            this._addresses$.next(addresses);
          } else {
            this._storage.clear();
            const initialHref = window.location.href;
            window.location.href = initialHref;
          }
        })
      )
  }

  private _setCurrentAddress(address: Address) {
    const { main } = address;

    if (main) {
      this._currentAddress = address;
      this._currentAddress$.next(address);
      this._storage.store(CURRRENT_ADDRESS_KEY, address);
    }
  }

  private _addNewAddress(address: Address) {
    return from(Device.getId())
      .pipe(
        switchMap((deviceId) => {
          const { identifier } = deviceId;

          return this._http
            .post('/addresses', {
              ...address,
              device_id: identifier,
            });
        }),
        tap((address: Address) => this._setCurrentAddress(address)),
        switchMap(() => this.getAddresses())
      )
  }

  public getCurrentAddress() {
    return this._storage
      .get(CURRRENT_ADDRESS_KEY)
      .then((currentAddress: Address) => {

        if (currentAddress) {
          this._currentAddress = currentAddress;
          this._currentAddress$.next(currentAddress);
        }

        return currentAddress;
      })
  }

  public initAddress(number: HouseNumber) {
    const { street, city, city_id, street_id } = this.newAddressRequest;

    if (street && city) {
      const address = {
        city,
        city_id,
        street,
        street_id,
        number: number.value,
        commission_point_id: number.id + '',
        main: true
      }

      return this._addNewAddress(address);
    } else {
      return of();
    }

  }

  public removeAddress(address: Address) {
    const { id } = address;
    // console.log('Data from address editing => ', address);

    return this._http
      .delete('/addresses/' + id, {})
      .pipe(
        switchMap(() => this.getAddresses())
      );

  }

  public editAddress(address: Address) {
    let { id, commission_point_id, ...requestParams } = address;
    // console.log('Data from address editing => ', address);

    return this._http
      .patch('/addresses/' + id, {
        ...requestParams
      })
      .pipe(
        tap((address: Address) => this._setCurrentAddress(address)),
        switchMap(() => this.getAddresses())
      );

  }

  public addNewAddress(address: Address) {
    const { id, ...restParams } = address;
    return this._addNewAddress(restParams);
  }

}
